/**
 * Open-Meteo Current Weather — free, no key, fast.
 * https://open-meteo.com/en/docs
 *
 * We fetch wind speed (mph) + wind direction (degrees, meteorological standard
 * = where the wind is COMING FROM) for a single lat/lng. Result is cached for
 * 10 minutes per coordinate so dragging the target doesn't hammer the API.
 *
 * The shot's "plays like" distance is then corrected using a Tour-grade rule
 * of thumb: roughly +1.5% per mph of headwind, –1% per mph of tailwind, and
 * 0% for pure crosswind. Crosswind moves the ball laterally — outside the
 * scope of a distance calc.
 */

const ENDPOINT = "https://api.open-meteo.com/v1/forecast";
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 min — weather drifts slowly
const cache = new Map(); // key = "lat,lng" → { value, ts }

function key(lat, lng) { return `${lat.toFixed(3)},${lng.toFixed(3)}`; }

/** Fetch the current wind for a point. Returns { speed_mph, direction_deg } or null on failure. */
export async function fetchWind(lat, lng) {
  if (lat == null || lng == null) return null;
  const k = key(lat, lng);
  const cached = cache.get(k);
  if (cached && Date.now() - cached.ts < CACHE_TTL_MS) return cached.value;
  try {
    const url = `${ENDPOINT}?latitude=${lat.toFixed(4)}&longitude=${lng.toFixed(4)}&current=wind_speed_10m,wind_direction_10m&wind_speed_unit=mph`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = await res.json();
    const speed = json?.current?.wind_speed_10m;
    const dir = json?.current?.wind_direction_10m;
    if (typeof speed !== "number" || typeof dir !== "number") return null;
    const value = { speed_mph: speed, direction_deg: dir };
    cache.set(k, { value, ts: Date.now() });
    return value;
  } catch { return null; }
}

/** Bearing (degrees clockwise from true north) from point A → point B. */
export function shotBearing(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;
  const φ1 = toRad(lat1), φ2 = toRad(lat2);
  const Δλ = toRad(lng2 - lng1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/** Headwind component (mph) along the shot bearing.
 *  Open-Meteo `direction_deg` = where the wind is coming FROM (meteorological).
 *  Headwind exists when wind_from is roughly aligned with shot_bearing. */
export function headwindComponent(wind, shotBearingDeg) {
  if (!wind || wind.speed_mph == null) return 0;
  const Δ = ((wind.direction_deg - shotBearingDeg + 540) % 360) - 180; // -180..180
  // cos(Δ): 1 when wind comes from straight ahead (full headwind), -1 when from straight behind.
  return wind.speed_mph * Math.cos((Δ * Math.PI) / 180);
}

/** Crosswind component (mph, signed). Positive = wind blows ball to the right. */
export function crosswindComponent(wind, shotBearingDeg) {
  if (!wind || wind.speed_mph == null) return 0;
  const Δ = ((wind.direction_deg - shotBearingDeg + 540) % 360) - 180;
  return wind.speed_mph * Math.sin((Δ * Math.PI) / 180);
}

/** Apply the Tour rule-of-thumb wind correction to a distance.
 *  Positive headwind (mph)  → ball plays longer: +1.5% per mph
 *  Negative (tailwind)      → ball plays shorter: -1.0% per |mph|
 *  Returns the new "club distance" the player should hit. */
export function windAdjustedYards(yards, headwindMph) {
  if (yards == null) return yards;
  if (!headwindMph) return yards;
  const pct = headwindMph > 0 ? 0.015 : 0.010;
  return Math.max(0, Math.round(yards * (1 + pct * headwindMph)));
}

/** Human-readable wind label given speed + direction relative to the shot.
 *  E.g. "8 mph headwind", "12 mph tailwind", "6 mph left-to-right". */
export function describeWind(wind, shotBearingDeg) {
  if (!wind) return "";
  const h = headwindComponent(wind, shotBearingDeg);
  const c = crosswindComponent(wind, shotBearingDeg);
  const absH = Math.abs(h), absC = Math.abs(c);
  if (absH < 2 && absC < 2) return `${Math.round(wind.speed_mph)} mph · calm`;
  if (absH >= absC * 1.2) {
    return h > 0
      ? `${Math.round(absH)} mph headwind`
      : `${Math.round(absH)} mph tailwind`;
  }
  if (absC >= absH * 1.2) {
    // c > 0  → wind comes from the right of the shot → pushes ball R → L
    // c < 0  → wind comes from the left  of the shot → pushes ball L → R
    return c > 0
      ? `${Math.round(absC)} mph R→L`
      : `${Math.round(absC)} mph L→R`;
  }
  // Mixed — show full speed + the dominant direction.
  if (h > 0) return `${Math.round(wind.speed_mph)} mph ${c > 0 ? "into R→L" : "into L→R"}`;
  return `${Math.round(wind.speed_mph)} mph ${c > 0 ? "down R→L" : "down L→R"}`;
}
